import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Coins,
  Trophy,
  TrendingUp,
  History,
  ArrowLeft,
  Settings,
  User,
  ExternalLink,
  Plus,
  Users,
  Copy,
  Check,
  Gift,
  Filter,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/useAuth";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import EditProfileModal from "@/components/EditProfileModal";
import BidModal from "@/components/BidModal";
import { Skeleton } from "@/components/ui/skeleton";

interface BidWithEvent {
  id: string;
  amount: number;
  prediction: boolean;
  created_at: string;
  event: {
    id: string;
    title: string;
    title_pt: string | null;
    status: string;
    outcome: boolean | null;
    yes_pool: number;
    no_pool: number;
    deadline: string;
    total_bids: number;
  };
}

interface ReferralStats {
  total: number;
  completed: number;
  pending: number;
  earned: number;
}

type BetHistoryFilter = "all" | "pending" | "won" | "lost";

const Profile = () => {
  const { user, profile, loading } = useAuth();
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bids, setBids] = useState<BidWithEvent[]>([]);
  const [loadingBids, setLoadingBids] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [localProfile, setLocalProfile] = useState<any>(profile);
  const [selectedEventForBid, setSelectedEventForBid] = useState<BidWithEvent["event"] | null>(null);
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [referralStats, setReferralStats] = useState<ReferralStats>({ total: 0, completed: 0, pending: 0, earned: 0 });
  const [historyFilter, setHistoryFilter] = useState<BetHistoryFilter>("all");

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    setLocalProfile(profile);
  }, [profile]);

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase.from("profiles").select("*").eq("user_id", user.id).maybeSingle();
    if (data) setLocalProfile(data);
  }, [user]);

  // Refresh profile data when page loads
  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user, fetchProfile]);

  // Fetch referral stats
  useEffect(() => {
    const fetchReferralStats = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from("referrals")
        .select("status, reward_amount")
        .eq("referrer_id", user.id);

      if (!error && data) {
        const completed = data.filter((r) => r.status === "completed").length;
        const pending = data.filter((r) => r.status !== "completed").length;
        const earned = data
          .filter((r) => r.status === "completed")
          .reduce((sum, r) => sum + Number(r.reward_amount), 0);
        setReferralStats({
          total: data.length,
          completed,
          pending,
          earned,
        });
      }
    };

    if (user) {
      fetchReferralStats();
    }
  }, [user]);

  const groupBidRows = useCallback((rows: BidWithEvent[]) => {
    const map = new Map<string, BidWithEvent>();

    for (const row of rows) {
      if (!row.event) continue;

      const key = `${row.event.id}:${row.prediction}`;
      const rowAmount = Number((row as any).amount) || 0;
      const existing = map.get(key);

      if (!existing) {
        map.set(key, { ...row, amount: rowAmount });
      } else {
        existing.amount = Number(existing.amount) + rowAmount;
        if (new Date(row.created_at) > new Date(existing.created_at)) {
          existing.created_at = row.created_at;
        }
      }
    }

    return Array.from(map.values()).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, []);

  useEffect(() => {
    const fetchBids = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from("bids")
        .select(
          `
          id,
          amount,
          prediction,
          created_at,
          event:events(id, title, title_pt, status, outcome, yes_pool, no_pool, deadline, total_bids)
        `,
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (!error && data) {
        setBids(groupBidRows(data as unknown as BidWithEvent[]));
      }
      setLoadingBids(false);
    };

    if (user) {
      fetchBids();
    }
  }, [user, groupBidRows]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  const stats = [
    {
      icon: Coins,
      label: "CallyCoins",
      value: localProfile?.duv_coins?.toLocaleString() || "1,000",
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      icon: Trophy,
      label: locale.language === "pt" ? "Vitórias" : "Wins",
      value: localProfile?.total_wins || 0,
      color: "text-success",
      bgColor: "bg-success/10",
    },
    {
      icon: TrendingUp,
      label: locale.language === "pt" ? "Total de Previsões" : "Total Predictions",
      value: localProfile?.total_bids || 0,
      color: "text-accent",
      bgColor: "bg-accent/10",
    },
  ];

  const getBidResult = (bid: BidWithEvent): "pending" | "won" | "lost" => {
    if (bid.event.status !== "RESOLVED") return "pending";
    return bid.prediction === bid.event.outcome ? "won" : "lost";
  };

  // Calculate potential/actual winnings for a bid
  const calculateWinnings = (bid: BidWithEvent) => {
    const totalPool = bid.event.yes_pool + bid.event.no_pool;
    const winningPool = bid.prediction ? bid.event.yes_pool : bid.event.no_pool;
    if (winningPool === 0) return 0;
    const share = bid.amount / winningPool;
    const platformFee = 0.05; // 5% platform fee
    return Math.floor(totalPool * share * (1 - platformFee));
  };

  // Filter and stats for bet history
  const filteredBids = useMemo(() => {
    if (historyFilter === "all") return bids;
    return bids.filter((bid) => getBidResult(bid) === historyFilter);
  }, [bids, historyFilter]);

  const betStats = useMemo(() => {
    const resolved = bids.filter((bid) => bid.event.status === "RESOLVED");
    const won = resolved.filter((bid) => bid.prediction === bid.event.outcome);
    const lost = resolved.filter((bid) => bid.prediction !== bid.event.outcome);
    const pending = bids.filter((bid) => bid.event.status !== "RESOLVED");
    
    const totalWagered = bids.reduce((sum, bid) => sum + bid.amount, 0);
    const totalWon = won.reduce((sum, bid) => sum + calculateWinnings(bid), 0);
    const totalLost = lost.reduce((sum, bid) => sum + bid.amount, 0);
    const netProfit = totalWon - lost.reduce((sum, bid) => sum + bid.amount, 0);
    const winRate = resolved.length > 0 ? (won.length / resolved.length) * 100 : 0;

    return {
      total: bids.length,
      won: won.length,
      lost: lost.length,
      pending: pending.length,
      totalWagered,
      totalWon,
      totalLost,
      netProfit,
      winRate,
    };
  }, [bids]);

  const handlePlaceAnotherBid = (event: BidWithEvent["event"], e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedEventForBid(event);
    setIsBidModalOpen(true);
  };

  const handleBidPlaced = async () => {
    // Refetch bids to update the list
    if (!user) return;
    const { data } = await supabase
      .from("bids")
      .select(
        `
        id,
        amount,
        prediction,
        created_at,
        event:events(id, title, title_pt, status, outcome, yes_pool, no_pool, deadline, total_bids)
      `,
      )
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (data) {
      setBids(groupBidRows(data as unknown as BidWithEvent[]));
    }
    // Also refetch profile for updated stats
    fetchProfile();
  };

  const referralLink = localProfile?.referral_code
    ? `${window.location.origin}/join/${localProfile.referral_code}`
    : "";

  const copyReferralLink = async () => {
    if (!referralLink) return;
    await navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast({
      title: locale.language === "pt" ? "Link copiado!" : "Link copied!",
      description:
        locale.language === "pt"
          ? "Seus amigos ganham 100 CallyCoins e você ganha 50 quando eles fizerem a primeira aposta!"
          : "Your friends get 100 CallyCoins and you earn 50 when they make their first bet!",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-6 text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/")}
          >
            <ArrowLeft size={18} className="mr-2" />
            {locale.language === "pt" ? "Voltar" : "Back"}
          </Button>

          {/* Header with Avatar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex items-center gap-4 sm:gap-6"
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-full bg-muted flex items-center justify-center overflow-hidden">
              {localProfile?.avatar_url ? (
                <img src={localProfile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <User size={28} className="text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <h1 className="text-xl sm:text-3xl md:text-4xl font-bold text-foreground truncate">
                  {localProfile?.username || (locale.language === "pt" ? "Meu Perfil" : "My Profile")}
                </h1>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditModalOpen(true)}
                  className="text-muted-foreground hover:text-foreground shrink-0"
                >
                  <Settings size={18} />
                </Button>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground truncate">{user?.email}</p>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-3 gap-2 sm:gap-4 mb-10"
          >
            {stats.map((stat, index) => (
              <div key={stat.label} className="glass-card p-3 sm:p-6 flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                <div className={`p-2 sm:p-3 rounded-xl ${stat.bgColor}`}>
                  <stat.icon size={20} className={`${stat.color} sm:w-6 sm:h-6`} />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-[10px] sm:text-sm text-muted-foreground">{stat.label}</p>
                  <p className={`text-lg sm:text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Invite Friends Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-10"
          >
            <div className="flex items-center gap-2 mb-4">
              <Users size={20} className="text-primary" />
              <h2 className="text-xl font-bold text-foreground">
                {locale.language === "pt" ? "Convide Amigos" : "Invite Friends"}
              </h2>
            </div>

            <div className="glass-card p-6">
              <p className="text-sm text-muted-foreground mb-4">
                {locale.language === "pt"
                  ? "Seus amigos ganham 100 CallyCoins ao se cadastrar! Você ganha 50 quando eles fizerem a primeira aposta."
                  : "Your friends get 100 CallyCoins when they sign up! You earn 50 when they make their first bet."}
              </p>

              <div className="flex gap-2 mb-4">
                <div className="flex-1 bg-muted/50 rounded-lg px-3 sm:px-4 py-3 text-xs sm:text-sm text-foreground font-mono truncate border border-border/50">
                  {referralLink || "Loading..."}
                </div>
                <Button
                  onClick={copyReferralLink}
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  disabled={!referralLink}
                >
                  {copied ? <Check size={18} className="text-success" /> : <Copy size={18} />}
                </Button>
              </div>

              {/* Share Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6">
                <Button
                  onClick={() => {
                    const text =
                      locale.language === "pt"
                        ? "Junte-se a mim no Callyout e ganhe 100 CallyCoins grátis!"
                        : "Join me on Callyout and get 100 free CallyCoins!";
                    window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + referralLink)}`, "_blank");
                  }}
                  variant="outline"
                  className="flex-1 gap-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 border-[#25D366]/30 text-[#25D366] text-sm"
                  disabled={!referralLink}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current shrink-0">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <span className="truncate">{locale.language === "pt" ? "Compartilhar no WhatsApp" : "Share on WhatsApp"}</span>
                </Button>
                <Button
                  onClick={() => {
                    const text =
                      locale.language === "pt"
                        ? "Junte-se a mim no Callyout e ganhe 100 CallyCoins grátis!"
                        : "Join me on Callyout and get 100 free CallyCoins!";
                    window.open(
                      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink || "")}`,
                      "_blank",
                    );
                  }}
                  variant="outline"
                  className="flex-1 gap-2 bg-foreground/5 hover:bg-foreground/10 border-foreground/20 text-sm"
                  disabled={!referralLink}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current shrink-0">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  <span className="truncate">{locale.language === "pt" ? "Compartilhar no X" : "Share on X"}</span>
                </Button>
              </div>

              {/* Stats Row */}
              <div className="flex gap-6 justify-center">
                <div className="text-center">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-2 mx-auto">
                    <Users size={20} className="text-primary" />
                  </div>
                  <p className="text-2xl font-bold text-foreground">{referralStats.completed}</p>
                  <p className="text-xs text-muted-foreground">
                    {locale.language === "pt" ? "Convidados" : "Referred"}
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-success/10 mb-2 mx-auto">
                    <Gift size={20} className="text-success" />
                  </div>
                  <p className="text-2xl font-bold text-success">{referralStats.earned}</p>
                  <p className="text-xs text-muted-foreground">
                    {locale.language === "pt" ? "DC Ganhos" : "DC Earned"}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bet History */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <History size={20} className="text-primary" />
                <h2 className="text-xl font-bold text-foreground">
                  {locale.language === "pt" ? "Histórico de Previsões" : "Prediction History"}
                </h2>
              </div>
            </div>

            {/* Stats Summary */}
            {!loadingBids && bids.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="glass-card p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-primary mb-1">
                    <TrendingUp size={16} />
                    <span className="text-xs text-muted-foreground">
                      {locale.language === "pt" ? "Taxa de Vitória" : "Win Rate"}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-foreground">{betStats.winRate.toFixed(1)}%</p>
                </div>
                <div className="glass-card p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-success mb-1">
                    <CheckCircle size={16} />
                    <span className="text-xs text-muted-foreground">
                      {locale.language === "pt" ? "Vitórias" : "Won"}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-success">{betStats.won}</p>
                </div>
                <div className="glass-card p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-destructive mb-1">
                    <XCircle size={16} />
                    <span className="text-xs text-muted-foreground">
                      {locale.language === "pt" ? "Perdas" : "Lost"}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-destructive">{betStats.lost}</p>
                </div>
                <div className="glass-card p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                    <Clock size={16} />
                    <span className="text-xs text-muted-foreground">
                      {locale.language === "pt" ? "Pendentes" : "Pending"}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-muted-foreground">{betStats.pending}</p>
                </div>
              </div>
            )}

            {/* Filter Tabs */}
            {!loadingBids && bids.length > 0 && (
              <Tabs value={historyFilter} onValueChange={(v) => setHistoryFilter(v as BetHistoryFilter)} className="mb-4">
                <TabsList className="grid w-full grid-cols-4 bg-card border border-border">
                  <TabsTrigger value="all" className="text-xs sm:text-sm">
                    {locale.language === "pt" ? "Todos" : "All"} ({betStats.total})
                  </TabsTrigger>
                  <TabsTrigger value="pending" className="text-xs sm:text-sm gap-1">
                    <Clock size={14} className="hidden sm:block text-muted-foreground" />
                    {locale.language === "pt" ? "Pendentes" : "Pending"} ({betStats.pending})
                  </TabsTrigger>
                  <TabsTrigger value="won" className="text-xs sm:text-sm gap-1">
                    <CheckCircle size={14} className="hidden sm:block text-success" />
                    {locale.language === "pt" ? "Vitórias" : "Won"} ({betStats.won})
                  </TabsTrigger>
                  <TabsTrigger value="lost" className="text-xs sm:text-sm gap-1">
                    <XCircle size={14} className="hidden sm:block text-destructive" />
                    {locale.language === "pt" ? "Perdas" : "Lost"} ({betStats.lost})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            {loadingBids ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="glass-card p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-6 w-12 rounded-full" />
                        <Skeleton className="h-5 w-16" />
                        <Skeleton className="h-6 w-16 rounded-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : bids.length === 0 ? (
              <div className="glass-card p-8 text-center text-muted-foreground">
                {locale.language === "pt"
                  ? "Você ainda não fez nenhuma previsão"
                  : "You haven't made any predictions yet"}
              </div>
            ) : filteredBids.length === 0 ? (
              <div className="glass-card p-8 text-center text-muted-foreground">
                {locale.language === "pt"
                  ? "Nenhuma previsão encontrada nesta categoria"
                  : "No predictions found in this category"}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredBids.map((bid, index) => {
                  const result = getBidResult(bid);
                  const eventTitle =
                    locale.language === "pt" && bid.event.title_pt ? bid.event.title_pt : bid.event.title;
                  const isEventOpen = bid.event.status === "OPEN";
                  const winnings = result === "won" ? calculateWinnings(bid) : 0;

                  return (
                    <motion.div
                      key={`${bid.event.id}-${bid.prediction}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="glass-card p-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <Link
                          to={`/event/${bid.event.id}`}
                          className="flex-1 group"
                        >
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                              {eventTitle}
                            </p>
                            <ExternalLink
                              size={14}
                              className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(bid.created_at).toLocaleDateString(locale.language === "pt" ? "pt-BR" : "en-US")}
                          </p>
                        </Link>

                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                          <span
                            className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${
                              bid.prediction ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
                            }`}
                          >
                            {bid.prediction
                              ? locale.language === "pt"
                                ? "SIM"
                                : "YES"
                              : locale.language === "pt"
                                ? "NÃO"
                                : "NO"}
                          </span>

                          <div className="text-right min-w-[60px] sm:min-w-[80px]">
                            <span className="text-primary font-semibold text-sm sm:text-base">
                              {bid.amount.toLocaleString()} DC
                            </span>
                            {result === "won" && winnings > 0 && (
                              <p className="text-xs text-success">
                                +{winnings.toLocaleString()} DC
                              </p>
                            )}
                          </div>

                          <span
                            className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold min-w-[60px] sm:min-w-[70px] text-center flex items-center gap-1 justify-center ${
                              result === "won"
                                ? "bg-success/20 text-success"
                                : result === "lost"
                                  ? "bg-destructive/20 text-destructive"
                                  : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {result === "won" ? (
                              <CheckCircle size={12} />
                            ) : result === "lost" ? (
                              <XCircle size={12} />
                            ) : (
                              <Clock size={12} />
                            )}
                            <span className="hidden sm:inline">
                              {result === "won"
                                ? locale.language === "pt"
                                  ? "Ganhou"
                                  : "Won"
                                : result === "lost"
                                  ? locale.language === "pt"
                                    ? "Perdeu"
                                    : "Lost"
                                  : locale.language === "pt"
                                    ? "Pendente"
                                    : "Pending"}
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* Increase bid button - only show for OPEN events */}
                      {isEventOpen && (
                        <div className="mt-3 pt-3 border-t border-border/50">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handlePlaceAnotherBid(bid.event, e)}
                            className="w-full sm:w-auto text-primary hover:text-primary hover:bg-primary/10"
                          >
                            <Plus size={16} className="mr-2" />
                            {locale.language === "pt" ? "Aumentar previsão" : "Increase prediction"}
                          </Button>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </main>

      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onProfileUpdated={fetchProfile}
      />

      <BidModal
        isOpen={isBidModalOpen}
        onClose={() => {
          setIsBidModalOpen(false);
          setSelectedEventForBid(null);
        }}
        event={selectedEventForBid}
        onBidPlaced={handleBidPlaced}
      />
    </div>
  );
};

export default Profile;
